import React, { createContext, useContext, useEffect, useState } from 'react';
import { useOktaAuth } from '@okta/okta-react';
import { supabase, setAuthToken } from '../lib/api';

const UserContext = createContext(null);

export const UserProvider = ({ children }) => {
  const { authState } = useOktaAuth();
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);

  // Effect 1: keep the API client token in sync with the current ID token.
  // Re-fires on every authState reference change (including silent renewals)
  // so PostgREST always sees a fresh JWT, but does not touch loading state.
  useEffect(() => {
    if (authState?.isAuthenticated) {
      setAuthToken(authState.idToken?.idToken || null);
    } else {
      setAuthToken(null);
    }
  }, [authState]);

  // Effect 2: fetch the employee profile when the authenticated user changes
  // (or first authenticates). Keyed off stable derived values (isAuthenticated +
  // email) so silent token renewals don't re-query the DB on every cycle.
  const isAuthenticated = authState?.isAuthenticated ?? false;
  const email = authState?.idToken?.claims?.email ?? null;

  useEffect(() => {
    if (!isAuthenticated) {
      setEmployee(null);
      setLoading(false);
      return;
    }

    if (!email) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    // Set loading true on each fresh fetch so we render the LOADING screen
    // (not ProfileNotFound or stale employee data) while the query is in flight.
    setLoading(true);

    (async () => {
      const { data, error } = await supabase
        .from('employees')
        .select('*')
        .eq('email', email)
        .eq('active', true)
        .single();
      if (cancelled) return;
      setEmployee(error || !data ? null : data);
      setLoading(false);
    })();

    return () => { cancelled = true; };
  }, [isAuthenticated, email]);

  return (
    <UserContext.Provider value={{ employee, loading, setEmployee }}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => useContext(UserContext);
